import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Battery, Zap, MapPin } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// AI-generated E3-branded charging station mockups (Gemini Nano Banana)
const IMG = {
    highway_hub: "/generated/e3_highway_hub.png",
    resort: "/generated/e3_resort_charger.png",
    urban_night: "/generated/e3_urban_night.png",
    premium_hub: "/generated/e3_premium_hub.png",
    fuel_station: "/generated/e3_fuel_station.png",
    tata: "/generated/indian_ev_tata_charging.png",
    mahindra: "/generated/indian_ev_mahindra.png",
    mg_hyundai: "/generated/indian_ev_mg_hyundai.png",
    byd: "/generated/indian_ev_byd_seal.png",
};

const NETWORK_TYPES = [
    {
        img: IMG.highway_hub,
        title: "Highway Charging Hubs",
        subtitle: "Fast charging every 100 km",
        desc: "120–360 kW ultra-fast bays under signature E3 canopies on national highways.",
        tag: "NH · Highway",
    },
    {
        img: IMG.resort,
        title: "Destination Charging",
        subtitle: "Hotels · Resorts · Restaurants",
        desc: "Premium destination charging designed for Indian hospitality partners.",
        tag: "Hospitality",
    },
    {
        img: IMG.urban_night,
        title: "Urban Charging Hubs",
        subtitle: "Metro cities · 24 × 7",
        desc: "Compact fast-charging bays for daily-driver EVs in Mumbai, Delhi, Bengaluru & beyond.",
        tag: "Urban",
    },
    {
        img: IMG.premium_hub,
        title: "Premium Charging Hubs",
        subtitle: "8 bays · lounge · solar canopy",
        desc: "Flagship E3 hubs with ultra-fast 240 kW bays, café, retail and solar integration.",
        tag: "Flagship",
    },
    {
        img: IMG.fuel_station,
        title: "Fuel Station Integration",
        subtitle: "Alongside conventional refuelling",
        desc: "Partnering with oil marketing companies to add E3 EV bays to existing forecourts.",
        tag: "OMC Partnership",
    },
];

// Indian-market EVs that will use the E3 network
const INDIAN_EVS = [
    {
        img: IMG.tata,
        model: "Tata Nexon EV LR",
        battery: "45 kWh",
        segment: "Compact SUV",
        badge: "DC Fast",
    },
    {
        img: IMG.mahindra,
        model: "Mahindra BE 6 · XEV 9e",
        battery: "59 / 79 kWh",
        segment: "Premium SUV",
        badge: "High-Speed",
    },
    {
        img: IMG.mg_hyundai,
        model: "MG ZS EV · Hyundai Creta EV",
        battery: "50 / 51 kWh",
        segment: "Family SUV",
        badge: "DC Fast",
    },
    {
        img: IMG.byd,
        model: "BYD Atto 3 · Seal",
        battery: "60 / 82 kWh",
        segment: "Global Premium",
        badge: "Ultra Fast",
    },
];

const FOCUS_POINTS = [
    "Long-range EV travel",
    "Premium charging experience",
    "Destination charging",
    "Highway charging",
    "Fleet charging",
    "Reliable uptime",
    "Future-ready infrastructure",
];

const BATTERY_DATA = [
    { model: "Tata Nexon EV LR", battery: "45 kWh", charging: "DC Fast Charging" },
    { model: "Tata Curvv EV", battery: "55 kWh", charging: "DC Fast Charging" },
    { model: "Mahindra BE 6", battery: "59 / 79 kWh", charging: "High-Speed DC" },
    { model: "Mahindra XEV 9e", battery: "59 / 79 kWh", charging: "High-Speed DC" },
    { model: "MG ZS EV", battery: "50.3 kWh", charging: "DC Fast Charging" },
    { model: "MG Windsor EV", battery: "38 / 52.9 kWh", charging: "DC Fast Charging" },
    { model: "BYD Atto 3", battery: "60.48 kWh", charging: "High-Speed DC" },
    { model: "BYD Seal", battery: "61.4 / 82.5 kWh", charging: "Ultra Fast Charging" },
    { model: "Hyundai Creta Electric", battery: "42 / 51.4 kWh", charging: "DC Fast Charging" },
    { model: "Kia EV6", battery: "77.4 kWh", charging: "Ultra Fast Charging" },
    { model: "Hyundai Ioniq 5", battery: "72.6 kWh", charging: "Ultra Fast Charging" },
    { model: "BMW iX1", battery: "66.4 kWh", charging: "Ultra Fast Charging" },
    { model: "Mercedes EQB", battery: "66.5 kWh", charging: "Ultra Fast Charging" },
    { model: "Volvo EX40", battery: "78 kWh", charging: "Ultra Fast Charging" },
    { model: "BYD Sealion 7", battery: "82.5 kWh", charging: "Ultra Fast Charging" },
];

const speedBadge = (charging) => {
    if (charging.includes("Ultra")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (charging.includes("High")) return "bg-blue-100 text-blue-800 border-blue-200";
    return "bg-stone-100 text-stone-700 border-stone-200";
};

export const About = () => {
    return (
        <div data-testid="about-page">
            {/* HERO with full-bleed E3 station mockup */}
            <section className="relative min-h-[80vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.highway_hub})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/40 to-stone-950/20" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Who We Are
                    </div>
                    <h1 data-testid="about-hero-title" className="font-serif text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-5xl animate-fade-up">
                        Building India's<br />
                        <span className="italic e3-gradient-text">premium</span> charging network.
                    </h1>
                    <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl mt-8 animate-fade-up">
                        Earth Energy Engine (E3) is designing one of India's largest privately
                        developed charging networks — engineered for long-range EVs, premium
                        destinations and the decade ahead.
                    </p>
                </div>
            </section>

            {/* Our Focus */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="e3-overline mb-4">Our Focus</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-stone-900 mb-6 leading-[1.05]">
                            Designed around what matters.
                        </h2>
                        <p className="text-stone-600 leading-relaxed">
                            Every square metre of an E3 station is engineered around one goal — the confidence of a long-range EV driver.
                        </p>
                    </div>
                    <div className="md:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {FOCUS_POINTS.map((p) => (
                                <div key={p} className="flex items-start gap-3 py-3 border-b border-stone-200">
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                                    <span className="text-stone-800">{p}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* NETWORK TYPES — E3-branded station mockups */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">The E3 Network</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Five station formats. <span className="italic e3-gradient-text">One brand.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                A cohesive design language across highways, resorts, urban hubs and fuel stations — instantly recognisable, engineered for uptime.
                            </p>
                        </div>
                    </div>

                    {/* Feature card + 4 grid */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="about-network-types">
                        {/* Feature card (highway) */}
                        <div className="md:col-span-7 group rounded-2xl overflow-hidden border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid="about-network-0">
                            <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                                <img src={NETWORK_TYPES[0].img} alt={NETWORK_TYPES[0].title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                            </div>
                            <div className="p-8">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="e3-overline">{NETWORK_TYPES[0].tag}</span>
                                    <span className="w-1 h-1 rounded-full bg-stone-400" />
                                    <span className="text-xs text-stone-500">{NETWORK_TYPES[0].subtitle}</span>
                                </div>
                                <h3 className="font-serif text-3xl font-medium text-stone-900 mb-3">{NETWORK_TYPES[0].title}</h3>
                                <p className="text-stone-600 leading-relaxed">{NETWORK_TYPES[0].desc}</p>
                            </div>
                        </div>

                        {/* Two stacked cards */}
                        <div className="md:col-span-5 grid grid-cols-1 gap-6">
                            {NETWORK_TYPES.slice(1, 3).map((n, i) => (
                                <div key={n.title} className="group rounded-2xl overflow-hidden border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid={`about-network-${i + 1}`}>
                                    <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                                        <img src={n.img} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                                    </div>
                                    <div className="p-6">
                                        <div className="e3-overline mb-2">{n.tag}</div>
                                        <h3 className="font-serif text-xl font-medium text-stone-900 mb-2">{n.title}</h3>
                                        <p className="text-stone-600 text-sm leading-relaxed">{n.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Remaining 2 wide */}
                        {NETWORK_TYPES.slice(3).map((n, i) => (
                            <div key={n.title} className="md:col-span-6 group rounded-2xl overflow-hidden border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid={`about-network-${i + 3}`}>
                                <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                                    <img src={n.img} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                                </div>
                                <div className="p-6">
                                    <div className="e3-overline mb-2">{n.tag}</div>
                                    <h3 className="font-serif text-2xl font-medium text-stone-900 mb-2">{n.title}</h3>
                                    <p className="text-stone-600 text-sm leading-relaxed">{n.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-8 text-xs text-stone-500 italic">
                        Renders shown are architectural mockups of E3-branded stations for illustration.
                    </p>
                </div>
            </section>

            {/* VISION & MISSION with urban night bg */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.urban_night})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/60" aria-hidden="true" />
                <div className="max-w-7xl mx-auto relative grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6 text-stone-400">Vision</div>
                        <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-[1.1] mb-4">
                            To become India's most trusted <span className="italic e3-gradient-text">premium EV charging network.</span>
                        </h3>
                    </div>
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6 text-stone-400">Mission</div>
                        <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-[1.1]">
                            Fast charging every <span className="italic e3-gradient-text">100 kilometres</span> across India's major highways and urban destinations.
                        </h3>
                    </div>
                </div>
            </section>

            {/* INDIAN EVs at E3 chargers */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Built for Indian EVs</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                The cars our network is <span className="italic e3-gradient-text">designed for.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                From Tata and Mahindra to MG, Hyundai and BYD — every major EV launching in India benefits from 120 kW+ fast charging on E3.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger" data-testid="about-car-showcase">
                        {INDIAN_EVS.map((car, i) => (
                            <div key={i} className="group rounded-2xl overflow-hidden border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-xl" data-testid={`about-car-${i}`}>
                                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                                    <img src={car.img} alt={car.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="e3-overline">{car.segment}</div>
                                        <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border font-semibold ${speedBadge(car.badge)}`}>{car.badge}</span>
                                    </div>
                                    <div className="font-serif text-lg text-stone-900 mb-1 leading-tight">{car.model}</div>
                                    <div className="text-xs text-stone-500 flex items-center gap-1.5">
                                        <Battery className="w-3.5 h-3.5" /> {car.battery}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why 120kW+ */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                        <div className="md:col-span-6">
                            <div className="e3-overline mb-6">Why 120 kW and above?</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Built for the vehicles of the <span className="italic e3-gradient-text">next decade.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 flex items-end">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Charging infrastructure built today should remain relevant for the next 10 years. E3 focuses on 120 kW, 180 kW, 240 kW and 360 kW chargers — engineered for tomorrow's 70–100+ kWh batteries.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 stagger">
                        {[
                            { kw: "120", label: "kW · Highway Base" },
                            { kw: "180", label: "kW · Highway Premium" },
                            { kw: "240", label: "kW · Ultra Fast" },
                            { kw: "360", label: "kW · Future-Ready" },
                        ].map((c) => (
                            <div key={c.kw} className="e3-card">
                                <div className="font-serif text-6xl md:text-7xl font-light e3-gradient-text mb-3">{c.kw}</div>
                                <div className="text-sm text-stone-600">{c.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BATTERY LANDSCAPE — light bg, fully visible */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">India's EV Battery Landscape</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                70–100+ kWh batteries are becoming standard.
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                30 kW and 60 kW chargers will become inadequate on highways. E3 is being built for the future.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm">
                        <Table data-testid="about-battery-table">
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100 border-stone-200">
                                    <TableHead className="text-stone-700 font-semibold text-xs tracking-widest uppercase py-5">Vehicle</TableHead>
                                    <TableHead className="text-stone-700 font-semibold text-xs tracking-widest uppercase">Battery Capacity</TableHead>
                                    <TableHead className="text-stone-700 font-semibold text-xs tracking-widest uppercase">Charging Capability</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {BATTERY_DATA.map((row, i) => (
                                    <TableRow key={i} className="border-stone-200 hover:bg-stone-50" data-testid={`about-battery-row-${i}`}>
                                        <TableCell className="text-stone-900 font-medium py-4">{row.model}</TableCell>
                                        <TableCell className="text-stone-700 tabular-nums">{row.battery}</TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${speedBadge(row.charging)}`}>
                                                <Zap className="w-3 h-3" /> {row.charging}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <p className="mt-6 text-sm text-stone-500 max-w-3xl">
                        Source: Public manufacturer specifications. Battery sizes indicative — variants may differ. Every vehicle above will benefit from 120 kW+ DC fast charging on the E3 network.
                    </p>
                </div>
            </section>

            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-stone-900 mb-8">
                        E3 is not just another EV charging company. It's an <span className="italic e3-gradient-text">infrastructure platform</span>.
                    </h2>
                    <Link to="/usp" data-testid="about-cta-usp" className="e3-btn-primary">
                        See What Makes E3 Different <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
};
